/**
 * Escritor de archivos ZIP **sin compresión** (método `store`).
 *
 * Existe por una razón concreta: un `.xlsx` es un ZIP con XML dentro, y el
 * botón «Excel» de las tablas tenía que producir un archivo que Excel abriera
 * limpio. Las alternativas eran peores: renombrar un CSV a `.xls` hace que
 * Excel avise de que el formato no coincide con la extensión —justo lo que no
 * quieres en una demo frente a un cliente—, y traer SheetJS son ~800 kB de
 * dependencia para exportar quince tablas.
 *
 * `store` en vez de `deflate` porque el navegador no trae un compresor
 * síncrono y `CompressionStream` obligaría a volver asíncrono todo el camino.
 * Las hojas de este tablero pesan decenas de kB: comprimirlas no cambia nada.
 */

/** Un archivo dentro del ZIP. La ruta usa `/`, incluso en Windows. */
export interface IZipEntry {
  ruta: string;
  contenido: string;
}

/* -------------------------------------------------------------------------- */
/*                                ANCHOR CRC32                                */
/* -------------------------------------------------------------------------- */

/** Tabla de CRC-32 (polinomio 0xEDB88320), calculada una sola vez. */
const CRC_TABLE: Uint32Array = (() => {
  const tabla = new Uint32Array(256);

  for (let i = 0; i < 256; i++) {
    let valor = i;

    for (let bit = 0; bit < 8; bit++) {
      valor = valor & 1 ? 0xedb88320 ^ (valor >>> 1) : valor >>> 1;
    }

    tabla[i] = valor >>> 0;
  }

  return tabla;
})();

const crc32 = (bytes: Uint8Array): number => {
  let crc = 0xffffffff;

  for (const byte of bytes) {
    crc = CRC_TABLE[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  }

  return (crc ^ 0xffffffff) >>> 0;
};

/* -------------------------------------------------------------------------- */
/*                              ANCHOR escritura                              */
/* -------------------------------------------------------------------------- */

/** Buffer que crece y sabe escribir enteros en little-endian, como pide el ZIP. */
class ByteWriter {

  private bytes: Array<number> = [];

  public get length(): number {
    return this.bytes.length;
  }

  public u16(valor: number): void {
    this.bytes.push(valor & 0xff, (valor >>> 8) & 0xff);
  }

  public u32(valor: number): void {
    this.bytes.push(valor & 0xff, (valor >>> 8) & 0xff, (valor >>> 16) & 0xff, (valor >>> 24) & 0xff);
  }

  public raw(datos: Uint8Array): void {
    for (const byte of datos) {
      this.bytes.push(byte);
    }
  }

  /**
   * Se construye sobre un `ArrayBuffer` explícito y no con
   * `new Uint8Array(array)`: desde TypeScript 5.7 el tipo lleva el búfer como
   * parámetro, y el genérico por omisión (`ArrayBufferLike`, que admite
   * `SharedArrayBuffer`) no lo acepta el constructor de `Blob`.
   */
  public toUint8Array(): Uint8Array<ArrayBuffer> {
    const buffer = new ArrayBuffer(this.bytes.length);
    const vista = new Uint8Array(buffer);

    vista.set(this.bytes);

    return vista;
  }
}

/**
 * Fecha y hora en formato MS-DOS, que es lo que guarda el ZIP.
 *
 * Se pasa como parámetro en vez de leer el reloj aquí dentro para que el
 * resultado sea reproducible cuando haga falta comprobarlo.
 */
const dosFecha = (fecha: Date): { hora: number; dia: number } => ({
  // Los segundos van en pasos de dos: el formato sólo les reserva 5 bits.
  hora: (fecha.getHours() << 11) | (fecha.getMinutes() << 5) | (fecha.getSeconds() >> 1),
  dia: ((fecha.getFullYear() - 1980) << 9) | ((fecha.getMonth() + 1) << 5) | fecha.getDate(),
});

/** Marca «el nombre viene en UTF-8», para que los acentos no se rompan. */
const FLAG_UTF8 = 0x0800;

/** Empaqueta las entradas en un ZIP listo para descargar. */
export const zipStore = (entradas: ReadonlyArray<IZipEntry>, fecha: Date = new Date()): Blob => {
  const encoder = new TextEncoder();
  const { hora, dia } = dosFecha(fecha);

  const cuerpo = new ByteWriter();
  const central = new ByteWriter();

  for (const entrada of entradas) {
    const nombre = encoder.encode(entrada.ruta);
    const datos = encoder.encode(entrada.contenido);
    const crc = crc32(datos);
    const offset = cuerpo.length;

    /* cabecera local */
    cuerpo.u32(0x04034b50);
    cuerpo.u16(20);          // versión necesaria
    cuerpo.u16(FLAG_UTF8);
    cuerpo.u16(0);           // método: store
    cuerpo.u16(hora);
    cuerpo.u16(dia);
    cuerpo.u32(crc);
    cuerpo.u32(datos.length); // comprimido = sin comprimir
    cuerpo.u32(datos.length);
    cuerpo.u16(nombre.length);
    cuerpo.u16(0);           // sin campo extra
    cuerpo.raw(nombre);
    cuerpo.raw(datos);

    /* entrada del directorio central */
    central.u32(0x02014b50);
    central.u16(20);         // versión con la que se creó
    central.u16(20);
    central.u16(FLAG_UTF8);
    central.u16(0);
    central.u16(hora);
    central.u16(dia);
    central.u32(crc);
    central.u32(datos.length);
    central.u32(datos.length);
    central.u16(nombre.length);
    central.u16(0);          // extra
    central.u16(0);          // comentario
    central.u16(0);          // disco
    central.u16(0);          // atributos internos
    central.u32(0);          // atributos externos
    central.u32(offset);
    central.raw(nombre);
  }

  /* fin del directorio central */
  const fin = new ByteWriter();
  fin.u32(0x06054b50);
  fin.u16(0);
  fin.u16(0);
  fin.u16(entradas.length);
  fin.u16(entradas.length);
  fin.u32(central.length);
  fin.u32(cuerpo.length);
  fin.u16(0);

  return new Blob(
    [cuerpo.toUint8Array(), central.toUint8Array(), fin.toUint8Array()],
    { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
  );
};
